import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Chip,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import { useNavigate } from 'react-router-dom';
import CustomPagination from 'app/widgets/Shared/Pagination';
import superadminServices from 'app/services/superadmin-services';
import userServices from 'app/services/user-services';
import { normalizePhoneInput } from 'app/utils/phone';
import {
  CreateButton,
  SaveButton,
  StandardFiltersBar,
  StandardPageHeader,
  StandardSearchField,
  ActionIconButton,
  ResponsiveDataTable,
} from 'app/shared/ui';

const EMPTY_FORM = {
  name: '',
  tin: '',
  contact_email: '',
  contact_phone: '',
  whatsapp_number: '',
  device_metrics_retention_days: 365,
};

const CompaniesIndex = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [navigatingCompanyId, setNavigatingCompanyId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  const [openForm, setOpenForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [actionMenuRecord, setActionMenuRecord] = useState(null);

  const params = useMemo(() => {
    const parts = [
      `page=${currentPage}`,
      `include_archived=true`,
      encodeURI('q[s][]=name asc'),
    ];

    if (searchQuery.trim()) {
      parts.push(encodeURI(`q[name_cont]=${searchQuery.trim()}`));
    }

    return `?${parts.join('&')}`;
  }, [currentPage, searchQuery]);

  const loadRecords = () => {
    setLoading(true);
    superadminServices.getCompanies(params)
      .then((resp) => {
        setRecords(resp?.data || []);
        setTotalPages(resp?.meta?.total_pages || 1);
      })
      .catch(() => {
        setNotice({ severity: 'error', text: 'No se pudo cargar empresas.' });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadRecords();
  }, [params]);

  const openCreate = () => {
    setEditingRecord(null);
    setForm({ ...EMPTY_FORM });
    setOpenForm(true);
  };

  const openEdit = (record) => {
    setEditingRecord(record);
    setForm({
      name: record?.name || '',
      tin: record?.tin || '',
      contact_email: record?.contact_email || '',
      contact_phone: record?.contact_phone || '',
      whatsapp_number: record?.whatsapp_number || '',
      device_metrics_retention_days: record?.device_metrics_retention_days || 365,
    });
    setOpenForm(true);
  };

  const openActionMenu = (event, record) => {
    setActionMenuAnchorEl(event.currentTarget);
    setActionMenuRecord(record);
  };

  const closeActionMenu = () => {
    setActionMenuAnchorEl(null);
    setActionMenuRecord(null);
  };

  const runActionFromMenu = (handler) => {
    const record = actionMenuRecord;
    closeActionMenu();
    if (record) handler(record);
  };

  const saveRecord = () => {
    if (!form?.name?.trim()) return;
    const retentionDays = Number.parseInt(form?.device_metrics_retention_days, 10);
    if (Number.isNaN(retentionDays) || retentionDays < 1) {
      setNotice({ severity: 'error', text: 'La retención de métricas debe ser un número mayor o igual a 1 día.' });
      return;
    }

    setSaving(true);
    const body = { company: { ...form, device_metrics_retention_days: retentionDays } };
    const request = editingRecord?.id
      ? superadminServices.updateCompany(editingRecord.id, body)
      : superadminServices.createCompany(body);

    request
      .then(() => {
        setNotice({ severity: 'success', text: editingRecord?.id ? 'Empresa actualizada.' : 'Empresa creada.' });
        setOpenForm(false);
        loadRecords();
      })
      .catch((error) => {
        const msg = error?.response?.data?.errors?.join(', ') || 'No se pudo guardar la empresa.';
        setNotice({ severity: 'error', text: msg });
      })
      .finally(() => setSaving(false));
  };

  const archiveCompany = (record) => {
    if (!window.confirm(`¿Archivar empresa ${record.name}?`)) return;

    superadminServices.deleteCompany(record.id, 'archive')
      .then(() => {
        setNotice({ severity: 'success', text: 'Empresa archivada.' });
        loadRecords();
      })
      .catch(() => setNotice({ severity: 'error', text: 'No se pudo archivar la empresa.' }));
  };

  const purgeCompany = (record) => {
    const accepted = window.confirm(
      `¿Archivar empresa ${record.name} y eliminar sus métricas históricas? Esta acción elimina los datos en device_metrics y es irreversible.`,
    );
    if (!accepted) return;

    superadminServices.deleteCompany(record.id, 'purge')
      .then((resp) => {
        setNotice({
          severity: 'warning',
          text: `Archivado y limpieza de métricas encolada. Job ID: ${resp?.job_id || 'N/A'}`,
        });
        loadRecords();
      })
      .catch(() => setNotice({ severity: 'error', text: 'No se pudo encolar la limpieza de métricas.' }));
  };

  const deleteCompany = (record) => {
    const accepted = window.confirm(
      `¿Eliminar definitivamente la empresa ${record.name}? Esta acción elimina la empresa y sus datos asociados, y no se puede deshacer.`,
    );
    if (!accepted) return;

    const confirmedName = window.prompt(`Para confirmar, escribe el nombre exacto de la empresa: ${record.name}`);
    if (confirmedName !== record.name) {
      setNotice({ severity: 'info', text: 'Eliminación cancelada: el nombre no coincide.' });
      return;
    }

    superadminServices.deleteCompany(record.id, 'delete')
      .then((resp) => {
        setNotice({
          severity: 'warning',
          text: `Eliminación definitiva encolada. Job ID: ${resp?.job_id || 'N/A'}`,
        });
        loadRecords();
      })
      .catch(() => setNotice({ severity: 'error', text: 'No se pudo encolar la eliminación definitiva.' }));
  };

  const openCompanyDetail = async (record, detailKind) => {
    if (!record?.id) return;

    const targetPath = detailKind === 'devices'
      ? `/superadmin/devices?company_id=${record.id}`
      : (detailKind === 'stores' ? '/automations/stores' : `/superadmin/users?company_id=${record.id}`);

    if (detailKind === 'devices') {
      navigate(targetPath);
      return;
    }

    if (record?.archived_at) {
      setNotice({
        severity: 'warning',
        text: 'No se puede abrir esta vista para empresas archivadas.',
      });
      return;
    }

    if (detailKind === 'users') {
      navigate(targetPath);
      return;
    }

    setNavigatingCompanyId(record.id);
    try {
      await userServices.setCurrentCompany(record.id);
      navigate(targetPath);
    } catch (error) {
      const msg = error?.response?.data?.errors?.join(', ')
        || 'No se pudo abrir el detalle para esta empresa.';
      setNotice({ severity: 'error', text: msg });
    } finally {
      setNavigatingCompanyId(null);
    }
  };

  const renderCountButton = (record, detailKind, value) => (
    <Button
      size="small"
      variant="text"
      onClick={() => openCompanyDetail(record, detailKind)}
      disabled={
        navigatingCompanyId === record?.id
        || ((detailKind === 'stores' || detailKind === 'users') && !!record?.archived_at)
      }
      sx={{ minWidth: 0, px: 0.25, textTransform: 'none', fontWeight: 700 }}
    >
      {value || 0}
    </Button>
  );

  const renderRowActions = (record) => (
    <ActionIconButton size="small" title="Acciones" onClick={(event) => openActionMenu(event, record)}>
      <MoreVertOutlinedIcon fontSize="small" />
    </ActionIconButton>
  );

  const columns = [
    {
      key: 'id',
      label: 'ID',
      render: (record) => record.id || '-',
    },
    {
      key: 'name',
      label: 'Nombre',
      render: (record) => record.name || '-',
    },
    {
      key: 'stores_count',
      label: 'Locales',
      render: (record) => renderCountButton(record, 'stores', record?.stores_count),
    },
    {
      key: 'devices_count',
      label: 'Dispositivos',
      render: (record) => renderCountButton(record, 'devices', record?.devices_count),
    },
    {
      key: 'users_count',
      label: 'Usuarios',
      render: (record) => renderCountButton(record, 'users', record?.users_count),
    },
    {
      key: 'contact_email',
      label: 'Email',
      render: (record) => record.contact_email || '-',
    },
    {
      key: 'contact_phone',
      label: 'Teléfono',
      render: (record) => record.contact_phone || '-',
    },
    {
      key: 'device_metrics_retention_days',
      label: 'Retención métricas',
      render: (record) => `${record.device_metrics_retention_days || 365} días`,
    },
    {
      key: 'status',
      label: 'Estado',
      render: (record) => (
        record.archived_at
          ? <Chip size="small" color="default" label="Archivada" />
          : <Chip size="small" color="success" label="Activa" />
      ),
    },
    {
      key: 'actions',
      label: 'Acciones',
      align: 'right',
      mobileHidden: true,
      render: (record) => renderRowActions(record),
    },
  ];

  return (
    <Box>
      <StandardPageHeader title="Superadministración · Empresas" />

      <StandardFiltersBar>
        <Grid container spacing={1.25} alignItems="center">
          <Grid item xs={12} md={9}>
            <StandardSearchField
              fullWidth
              placeholder="Buscar empresa"
              onChange={(e) => {
                setCurrentPage(1);
                setSearchQuery(e.target.value || '');
              }}
            />
          </Grid>
          <Grid item xs={12} md={3} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <CreateButton onClick={openCreate}>
              Nueva empresa
            </CreateButton>
          </Grid>
        </Grid>
      </StandardFiltersBar>

      {!!notice && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      )}

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <ResponsiveDataTable
            columns={columns}
            rows={records}
            rowKey="id"
            emptyMessage="No hay empresas para mostrar."
            mobileTitle={(record) => record.name || '-'}
            mobileSubtitle={(record) => record.contact_email || 'Sin email de contacto'}
            renderRowActions={(record) => renderRowActions(record)}
          />
        )}
      </Paper>

      <Menu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl)}
        onClose={closeActionMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => runActionFromMenu(openEdit)}>
          <ListItemIcon>
            <EditOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Editar empresa" />
        </MenuItem>
        <MenuItem onClick={() => runActionFromMenu(archiveCompany)}>
          <ListItemIcon>
            <ArchiveOutlinedIcon fontSize="small" color="warning" />
          </ListItemIcon>
          <ListItemText primary="Archivar" />
        </MenuItem>
        <MenuItem onClick={() => runActionFromMenu(purgeCompany)}>
          <ListItemIcon>
            <DeleteOutlineOutlinedIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary="Archivar y purgar métricas" />
        </MenuItem>
        <MenuItem onClick={() => runActionFromMenu(deleteCompany)}>
          <ListItemIcon>
            <DeleteForeverOutlinedIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary="Eliminar definitivamente" />
        </MenuItem>
      </Menu>

      <CustomPagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageSelect={setCurrentPage}
      />

      <Dialog open={openForm} onClose={() => setOpenForm(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingRecord?.id ? 'Editar empresa' : 'Nueva empresa'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Nombre"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="TIN"
                value={form.tin}
                onChange={(e) => setForm({ ...form, tin: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email contacto"
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Teléfono contacto"
                value={form.contact_phone}
                onChange={(e) => setForm({ ...form, contact_phone: normalizePhoneInput(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="WhatsApp"
                value={form.whatsapp_number}
                onChange={(e) => setForm({ ...form, whatsapp_number: normalizePhoneInput(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="Retención métricas (días)"
                inputProps={{ min: 1 }}
                helperText="Por defecto 365 días (1 año)."
                value={form.device_metrics_retention_days}
                onChange={(e) => setForm({ ...form, device_metrics_retention_days: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Cancelar</Button>
          <SaveButton loading={saving} disabled={saving || !form.name.trim()} onClick={saveRecord}>
            Guardar
          </SaveButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompaniesIndex;
